import { Fragment, useEffect, useRef } from "react";
import "./agentflow.scss";
import { useReveal } from "../../hooks/useReveal";

const NODES = [
  {
    icon: "💬",
    label: "User Intent",
    color: "#3b82f6",
    items: ["Natural language", "Task or goal", "Context injection"],
  },
  {
    icon: "🤖",
    label: "Orchestrator",
    color: "#dc143c",
    items: ["Plan & reason", "Delegate subtasks", "Track memory"],
  },
  {
    icon: "🔧",
    label: "Tool Layer",
    color: "#8b5cf6",
    items: ["MCP servers", "RAG / vector DB", "Code execution"],
  },
  {
    icon: "🧠",
    label: "LLM Core",
    color: "#10b981",
    items: ["Claude / GPT-4o", "Tool call synthesis", "Chain-of-thought"],
  },
  {
    icon: "✅",
    label: "Output",
    color: "#f59e0b",
    items: ["Structured response", "Action executed", "Next turn ready"],
  },
];

const STATS = [
  { value: 8, suffix: "+", label: "Years Experience" },
  { value: 50, suffix: "+", label: "Apps Shipped" },
  { value: 15, suffix: "+", label: "AI Tools Built" },
  { value: 4, suffix: "", label: "LLM Providers" },
];

function Counter({ target, suffix }) {
  const ref = useRef();
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let current = 0;
          const step = target / 60;
          const timer = setInterval(() => {
            current += step;
            if (current >= target) {
              el.textContent = target + suffix;
              clearInterval(timer);
            } else {
              el.textContent = Math.floor(current) + suffix;
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, suffix]);

  return <span ref={ref}>0{suffix}</span>;
}

export default function AgentFlow() {
  const [sectionRef, visible] = useReveal(0.08);

  return (
    <div className="agentflow" id="agentflow" ref={sectionRef}>
      <div className="af-inner">
        <div className={`af-header ${visible ? "visible" : ""}`}>
          <h1>How I Build Agentic Systems</h1>
          <p className="af-subtitle">
            A look inside the architecture of a production AI agent — from raw
            user intent to structured, actionable output.
          </p>
        </div>

        <div className="flow-scroll">
        <div className="flow">
          {NODES.map((node, i) => (
            <Fragment key={node.label}>
              <div
                className={`node ${visible ? "visible" : ""}`}
                style={{
                  "--node-color": node.color,
                  transitionDelay: visible ? `${i * 0.12}s` : "0s",
                }}
              >
                <div className="nodeIcon">{node.icon}</div>
                <div className="nodeLabel">{node.label}</div>
                <ul>
                  {node.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              {i < NODES.length - 1 && (
                <div
                  className={`connector ${visible ? "visible" : ""}`}
                  style={{ transitionDelay: visible ? `${i * 0.12 + 0.08}s` : "0s" }}
                >
                  <div className="cLine" />
                  <div className="cArrow">›</div>
                </div>
              )}
            </Fragment>
          ))}
        </div>
        </div>

        <div className={`stats ${visible ? "visible" : ""}`}>
          {STATS.map((s, i) => (
            <div
              className="statItem"
              key={s.label}
              style={{ transitionDelay: visible ? `${0.6 + i * 0.1}s` : "0s" }}
            >
              <div className="statValue">
                <Counter target={s.value} suffix={s.suffix} />
              </div>
              <div className="statLabel">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
